// Per-client Calendar tab on the Client Profile: the same AxisCare-style month
// grid the Home Health Calendar page uses, filtered to this client, with the
// client's weekly authorizations beside it and a list toggle. One link opens
// the full Calendar page already filtered to the client.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subDays, addDays } from 'date-fns';
import useVisits, { matchesClient } from '../../hooks/useVisits';
import MonthGrid, { Legend } from '../visits/MonthGrid';
import AuthorizationsPanel from '../visits/AuthorizationsPanel';
import VisitList from '../visits/VisitList';

const iso = (d) => format(d, 'yyyy-MM-dd');
const seg = (active) => ({ padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: active ? 'var(--accent)' : 'transparent', color: active ? 'var(--white)' : 'var(--text3)' });

export function ClientVisitsView({ client }) {
  const [view, setView] = useState('calendar');
  const [month, setMonth] = useState(startOfMonth(new Date()));

  const range = useMemo(() => view === 'calendar'
    ? { start: iso(startOfWeek(startOfMonth(month), { weekStartsOn: 0 })), end: iso(endOfWeek(endOfMonth(month), { weekStartsOn: 0 })) }
    : { start: iso(subDays(startOfMonth(new Date()), 7)), end: iso(addDays(new Date(), 14)) },
  [view, month]);

  const { visits, loading, error } = useVisits(range, { includeUnassigned: view === 'calendar' });
  const mine = useMemo(() => visits.filter(v => matchesClient(v, client)), [visits, client]);

  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <button style={seg(view === 'calendar')} onClick={() => setView('calendar')}>Calendar</button>
            <button style={seg(view === 'list')} onClick={() => setView('list')}>List</button>
          </div>
          {view === 'calendar' && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setMonth(m => addMonths(m, -1))} aria-label="Previous month">‹</button>
              <span style={{ fontSize: 13, fontWeight: 500, minWidth: 110, textAlign: 'center' }}>{format(month, 'MMMM yyyy')}</span>
              <button className="btn btn-outline btn-sm" onClick={() => setMonth(m => addMonths(m, 1))} aria-label="Next month">›</button>
              <button className="btn btn-outline btn-sm" onClick={() => setMonth(startOfMonth(new Date()))}>Today</button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link to={`/hh/calendar?client=${client.id}`} className="btn btn-outline btn-sm">Open full calendar →</Link>
          <span style={{ fontSize: 9, background: 'var(--accent)', color: 'var(--white)', padding: '2px 7px', borderRadius: 4, fontWeight: 500 }}>AxisCare</span>
        </div>
      </div>

      {error && <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warn)', marginBottom: 12 }}>⚠ {error}</div>}

      {loading ? (
        <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 20 }}>Loading visits…</p>
      ) : view === 'list' ? (
        mine.length === 0 ? <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 20 }}>No visits for this client in this window.</p> : <VisitList visits={mine} mode="standard" />
      ) : (
        <>
          <MonthGrid visits={mine} month={month} groupBy="service" />
          {mine.length === 0 && !client?.axiscare_client_id && (
            <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>This client isn't linked to an AxisCare record yet, so matching falls back to name.</p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 380px) minmax(0, 1fr)', gap: 12, marginTop: 12, alignItems: 'start' }}>
            <div>
              <Legend />
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>Day headers show scheduled hours. Week column: S scheduled · B billable (clocked).</div>
            </div>
            <AuthorizationsPanel client={client} visits={mine} month={month} />
          </div>
        </>
      )}
    </div>
  );
}

export default ClientVisitsView;
